import type {
  ApiContext,
  ApiNotAuthorized,
  ClaimedTable,
  EventNotFound,
  ReservationRedeemRefused,
  StoreNotFound,
  StoreRoleForbidden,
  TableClaimRefused,
  TableKindNotFound,
  TableOrderNotFound,
  TablePayment,
  VendorShows,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { LocationsRepository } from "../repositories/locations-repository";
import type { BackendApi } from "./api";

interface ShowsCall {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  /** The shop, never a `loc_` entry id: a table is the whole shop's. */
  storeId: string;
}

export function loadVendorShows(o: ShowsCall): AsyncResult<VendorShows, ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.shows.list(ctx, o.storeId));
}

/** Holds a table at another shop's show; the answer carries its payment, unless the table costs nothing. */
export function claimTable(
  o: ShowsCall & { eventId: string; kindId: string },
): AsyncResult<
  ClaimedTable,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound | TableKindNotFound | TableClaimRefused
> {
  return o.authHandler.callApi((ctx) => o.client.shows.claimTable(ctx, o.storeId, o.eventId, o.kindId));
}

/** The payment for the table the shop holds: the same intent while the hold lasts. */
export function openTablePayment(
  o: ShowsCall & { orderId: string },
): AsyncResult<
  TablePayment,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | TableOrderNotFound | TableClaimRefused
> {
  return o.authHandler.callApi((ctx) => o.client.shows.payment(ctx, o.storeId, o.orderId));
}

/** Gives up a held table before paying for it. */
export function cancelTableOrder(
  o: ShowsCall & { orderId: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | TableOrderNotFound | TableClaimRefused> {
  return o.authHandler.callApi((ctx) => o.client.shows.cancelOrder(ctx, o.storeId, o.orderId));
}

/** Puts a table reserved on the storefront for an email on the shop, by its code. */
export function redeemReservation(
  o: ShowsCall & { code: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | ReservationRedeemRefused> {
  return o.authHandler.callApi((ctx) => o.client.shows.redeemReservation(ctx, o.storeId, o.code));
}

/** A paid table brings the shop's booth at the show, so its locations are read again. */
export function noteTablePaid(o: { locationsRepository: LocationsRepository }): void {
  o.locationsRepository.markStale();
}
